import type { ReactNode } from "react";
import type React from "react";
import { Children } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, getBrightnessHEX, setFontVariant } from "@similarweb/styles";

import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const SERP_ICON_SIZE = 24;
const SERP_ICON_OVERLAP = 14;

const SerpIcon = styled.div<{ background: string; onClick?: Function }>`
    width: ${SERP_ICON_SIZE}px;
    height: ${SERP_ICON_SIZE}px;
    border-radius: ${SERP_ICON_SIZE}px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    background: ${({ background }) => background};
    cursor: ${({ onClick }) => (typeof onClick === "function" ? "pointer" : "initial")};
    ${SWReactIcons} {
        display: flex;
        align-items: center;
        svg {
            path{ fill: ${({ background }) =>
                getBrightnessHEX(background) === "dark" ? colorsPalettes.carbon[0] : colorsPalettes.carbon[400]}
             }
        }
    }
`;

export const SerpIconComponent: React.FC<{
    background?: string;
    iconName: IconName;
    onClick?: (serp) => void;
}> = ({ background = colorsPalettes.carbon[0], ...props }) => {
    return (
        <SerpIcon
            background={background}
            onClick={props.onClick}
        >
            <SWReactIcons
                iconName={props.iconName}
                size="xs"

            />
        </SerpIcon>
    );
};

const createStackedSerpIconStyle = (count) => {
    let res = ``;
    for (let i = 0; i <= count; i += 1) {
        res += `
        &:nth-child(${i + 1}) {
          transform: translateX(${-1 * i * SERP_ICON_OVERLAP}px);
          z-index: ${SERP_ICON_SIZE - SERP_ICON_OVERLAP - i};
          svg {
            opacity: ${i === 0 ? 1 : 0};
          }
         }
      `;
    }
    return res;
};

const SerpIconsStackedContainer = styled.div<{ childrenCount?: number }>`
    position: relative;
    display: flex;
    align-items: center;
    width: ${({ childrenCount = 0 }) => SERP_ICON_SIZE + (childrenCount - 1) * (SERP_ICON_SIZE - SERP_ICON_OVERLAP)}px;
    margin-right: 4px;
    z-index: 0;
    ${SerpIcon} {
        position: relative;
        flex-shrink: 0;
        ${createStackedSerpIconStyle(5)}
    }
`;

const SerpIconsGroupedContainer = styled.div`
    display: flex;
    align-items: center;
    ${SerpIcon} {
        margin-right: 4px;
    }
`;

const SerpIconsComponentWithTextContainer = styled(FlexRow)`
    ${setFontVariant("body2", colorsPalettes.carbon["500"])};
    ${SerpIcon} {
        margin-right: 4px;
    }
`;

export const SerpIconsStacked: React.FC<{ numOfChildes?: number; children: ReactNode }> = (props) => {
    const childrenCount = props.numOfChildes ?? Children.toArray(props.children).length;
    return <SerpIconsStackedContainer childrenCount={childrenCount}>{props.children}</SerpIconsStackedContainer>;
};

export const SerpIconsGrouped: React.FC<{ children: ReactNode }> = (props) => {
    return <SerpIconsGroupedContainer>{props.children}</SerpIconsGroupedContainer>;
};

export const SerpIconsComponentWithText: React.FC<{ text: string; children: ReactNode }> = (props) => {
    return (
        <SerpIconsComponentWithTextContainer alignItems="center">
            {props.children}
            {props.text}
        </SerpIconsComponentWithTextContainer>
    );
};
